import {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  autoPlacement,
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import { PopoverProps, PopoverRef, PopoverTrigger } from '../../utils/popover.types';

import { PopoverContext } from './popover-root.context';
import { PopoverContextType } from './popover-root.types';

function PopoverRootInternal(
  {
    children,
    offset = 8,
    placement = 'bottom-start',
    useAutoPlacement = false,
    trigger = PopoverTrigger.Click,
    disabled = false,
    onOpenChange,
  }: PropsWithChildren<PopoverProps>,
  forwardedRef?: ForwardedRef<PopoverRef>,
) {
  const [open, setOpen] = useState(false);
  const id = useId();

  const floatingOpt: UseFloatingOptions = {
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    placement, // Does not work with autoPlacement
    middleware: [
      floatingOffset(offset),
      shift(),
      useAutoPlacement ? autoPlacement() : flip(),
    ],
  };

  const floating = useFloating(floatingOpt);
  const hover = useHover(floating.context, {
    enabled: !disabled && trigger === PopoverTrigger.Hover,
  });
  const focus = useFocus(floating.context, {
    enabled: !disabled && trigger === PopoverTrigger.Focus,
  });
  const click = useClick(floating.context, {
    enabled: !disabled && trigger === PopoverTrigger.Click,
  });
  const dismiss = useDismiss(floating.context);
  const role = useRole(floating.context);
  const interactions = useInteractions([click, dismiss, role, hover, focus]);

  const value: PopoverContextType = {
    ...floating,
    open,
    setOpen,
    labelId: `${id}-label`,
    descriptionId: `${id}-desc`,
    ...interactions,
  };

  useEffect(() => {
    if (open) {
      floating.update();
    }
  }, [floating, open]);

  // Fire onOpenChange only on an actual open/close transition. `floating` and an inline
  // `onOpenChange` both churn every render (autoUpdate; a debounced callback prop), so a
  // reactive dep would re-run this every render — a guarded no-op that also blurs the
  // edge-only intent. Hold the latest callback in a ref (mirrors displayRef in
  // useDropdownRhf) so the dep array is just `[open]`.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const prevOpenRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevOpenRef.current === open) return;
    prevOpenRef.current = open;
    onOpenChangeRef.current?.(open);
  }, [open]);

  useImperativeHandle(forwardedRef, () => ({ open, setOpen }), [open]);

  return <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>;
}

export const PopoverRoot = forwardRef(PopoverRootInternal);
